import type {
  AuthProviderId,
  ProvidersResponse,
  SessionResponse,
} from "./auth.types";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { rememberReturnPath } from "./returnPath";

import { apiRequest, setUnauthorizedHandler } from "@/shared/api/client";
import { siteConfig } from "@/config/site";

export const authKeys = {
  session: ["auth", "session"] as const,
  providers: ["auth", "providers"] as const,
};

type AuthContextValue = {
  session: SessionResponse | undefined;
  providers: ProvidersResponse["data"];
  providersLoading: boolean;
  isLoading: boolean;
  beginSignIn: (provider: AuthProviderId, returnPath?: string) => void;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<SessionResponse | undefined>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionQuery = useQuery({
    queryKey: authKeys.session,
    queryFn: () => apiRequest<SessionResponse>("/auth/session"),
    staleTime: 30_000,
    retry: false,
  });
  const providersQuery = useQuery({
    queryKey: authKeys.providers,
    queryFn: () => apiRequest<ProvidersResponse>("/auth/providers"),
    staleTime: 5 * 60_000, // 5 minutes
    retry: false,
  });

  useEffect(() => {
    let handling = false;

    setUnauthorizedHandler(() => {
      if (handling || location.pathname === "/signin") return;
      handling = true;
      const requested = `${location.pathname}${location.search}${location.hash}`;

      rememberReturnPath(requested);
      queryClient.setQueryData<SessionResponse>(authKeys.session, {
        isAuthenticated: false,
        user: null,
        activeTenant: null,
      });
      void queryClient.removeQueries({ queryKey: ["tenant"] });
      navigate("/signin", { replace: true, state: { returnTo: requested } });
    });

    return () => setUnauthorizedHandler(undefined);
  }, [location, navigate, queryClient]);

  const beginSignIn = useCallback(
    (provider: AuthProviderId, returnPath?: string) => {
      if (returnPath) rememberReturnPath(returnPath);
      window.location.assign(
        `${siteConfig.apiBaseUrl}/auth/${provider}/signin`,
      );
    },
    [],
  );

  const signOut = useCallback(async () => {
    await apiRequest<void>("/auth/signout", { method: "POST" });
    queryClient.clear();
    navigate("/", { replace: true });
  }, [navigate, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session: sessionQuery.data,
      providers: providersQuery.data?.data ?? [],
      providersLoading: providersQuery.isLoading,
      isLoading: sessionQuery.isLoading,
      beginSignIn,
      signOut,
      refreshSession: async () => (await sessionQuery.refetch()).data,
    }),
    [
      beginSignIn,
      providersQuery.data,
      providersQuery.isLoading,
      sessionQuery,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) throw new Error("useAuth must be used within AuthProvider");

  return context;
}
